import React from 'react';
import { connect } from 'react-redux';
import { withStyles } from 'tss-react/mui';
import { createProfilePanelButtonEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { login, logout } from '../../../authentication/actions.web';
import Avatar from '../../../base/avatar/components/Avatar';
import AbstractDialogTab from '../../../base/dialog/components/web/AbstractDialogTab';
import { translate } from '../../../base/i18n/functions';
import Button from '../../../base/ui/components/web/Button';
import Input from '../../../base/ui/components/web/Input';
import logger from '../../logger';
const styles = (theme) => {
    return {
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: '0 2px'
        },
        avatarContainer: {
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            marginBottom: theme.spacing(4)
        },
        bottomMargin: {
            marginBottom: theme.spacing(4)
        },
        label: {
            color: `${theme.palette.text01} !important`,
            ...theme.typography.bodyShortRegular,
            marginBottom: theme.spacing(2)
        },
        name: {
            marginBottom: theme.spacing(1)
        }
    };
};
const AUTHKEY_PEM_BEGIN = '-----BEGIN AuthKey-----';
const AUTHKEY_PEM_END = '-----END AuthKey-----';
const AUTHKEY_REQUIRED_PATTERN = /\S/;
/**
 * Checks whether the AuthKey credential field has a non-whitespace value.
 *
 * @param {unknown} value - The field value to check.
 * @returns {boolean}
 */
export function hasAuthKeyCredential(value) {
    return AUTHKEY_REQUIRED_PATTERN.test(String(value ?? ''));
}
/**
 * Checks whether an AuthKey credential is present without its private passphrase.
 *
 * @param {unknown} credential - The AuthKey credential value.
 * @param {unknown} credentialPassword - The AuthKey private passphrase value.
 * @returns {boolean}
 */
export function isAuthKeyCredentialPasswordMissing(credential, credentialPassword) {
    return hasAuthKeyCredential(credential) && !AUTHKEY_REQUIRED_PATTERN.test(String(credentialPassword ?? ''));
}
function base64ToText(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
}
function parseAuthKeyCredentialFile(value) {
    const text = String(value || '').trim();
    let payload;
    if (text.startsWith(AUTHKEY_PEM_BEGIN) && text.includes(AUTHKEY_PEM_END)) {
        const base64Payload = text
            .replace(AUTHKEY_PEM_BEGIN, '')
            .replace(AUTHKEY_PEM_END, '')
            .replace(/\s+/g, '');
        payload = JSON.parse(base64ToText(base64Payload));
    }
    else {
        payload = JSON.parse(text);
    }
    return {
        credential: payload.credentialEnc || payload.credential_enc || payload.credential || '',
        email: payload.email || payload.uid || payload.id || ''
    };
}
/**
 * React {@code Component} for modifying the local user's profile.
 *
 * @augments Component
 */
class ProfileTab extends AbstractDialogTab {
    static defaultProps = {
        displayName: '',
        email: '',
        // AuthKey credential fields.
        credential: '',
        credentialPassword: '',
        isDisplayedOnWelcomePage: false
    };
    /**
     * Initializes a new {@code ConnectedSettingsDialog} instance.
     *
     * @param {IProps} props - The React {@code Component} props to initialize
     * the new {@code ConnectedSettingsDialog} instance with.
     */
    constructor(props) {
        super(props);
        // Bind event handlers so they are only bound once for every instance.
        this._onAuthToggle = this._onAuthToggle.bind(this);
        this._onDisplayNameChange = this._onDisplayNameChange.bind(this);
        this._onEmailChange = this._onEmailChange.bind(this);
        // Bind event handlers for AuthKey service.
        this._onCredentialChange = this._onCredentialChange.bind(this);
        this._onCredentialFileChange = this._onCredentialFileChange.bind(this);
        this._onCredentialPasswordChange = this._onCredentialPasswordChange.bind(this);
        this._onChooseCredentialFileClick = this._onChooseCredentialFileClick.bind(this);
        this._setCredentialFromFile = this._setCredentialFromFile.bind(this);
    }
    /**
     * Changes display name of the user.
     *
     * @param {string} value - The key event to handle.
     *
     * @returns {void}
     */
    _onDisplayNameChange(value) {
        super._onChange({ displayName: value });
    }
    /**
     * Changes email of the user.
     *
     * @param {string} value - The key event to handle.
     *
     * @returns {void}
     */
    _onEmailChange(value) {
        super._onChange({ email: value });
    }
    // AuthKey credential helpers.
    /**
     * Changes credential of the user.
     *
     * @param {string} value - The credential value.
     * @returns {void}
     */
    _onCredentialChange(value) {
        super._onChange({ credential: value });
    }
    /**
     * Changes the password of credential of the user.
     *
     * @param {string} value - The credential password value.
     * @returns {void}
     */
    _onCredentialPasswordChange(value) {
        super._onChange({ credentialPassword: value });
    }
    /**
     * Handles the credential file selected from local storage.
     *
     * @param {Event} event - The file input change event.
     * @returns {void}
     */
    _onCredentialFileChange(event) {
        const input = event.target;
        if (!input) {
            return;
        }
        const credentialFile = input.files?.[0];
        if (!credentialFile || !window.FileReader) {
            return;
        }
        const credentialReader = new FileReader();
        credentialReader.onload = e => {
            try {
                const credentialFileInfo = parseAuthKeyCredentialFile(String(e.target?.result || ''));
                if (credentialFileInfo.email && credentialFileInfo.credential) {
                    this._setCredentialFromFile(credentialFileInfo.email, credentialFileInfo.credential);
                }
            }
            catch (error) {
                logger.warn('[authkey] Failed to parse credential file', error);
            }
            finally {
                input.value = '';
            }
        };
        credentialReader.onerror = error => {
            logger.warn('[authkey] Failed to read credential file', error);
            input.value = '';
        };
        credentialReader.onabort = () => {
            input.value = '';
        };
        credentialReader.readAsText(credentialFile, 'utf-8');
    }
    /**
     * Opens the credential file picker.
     *
     * @returns {void}
     */
    _onChooseCredentialFileClick() {
        document.getElementById('real-btn-choose-credential')?.click();
    }
    /**
     * Sets the credential from the file chosen by user.
     *
     * @param {string} email - The email parsed from the credential file.
     * @param {string} credential - The credential parsed from the file.
     * @returns {void}
     */
    _setCredentialFromFile(email, credential) {
        super._onChange({ email, credential });
    }
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { authEnabled, displayName, email, hideEmailInSettings, id, readOnlyName, 
        // AuthKey credential fields.
        credential, credentialPassword, isDisplayedOnWelcomePage, t } = this.props;
        const classes = withStyles.getClasses(this.props);
        const hiddenStyle = {
            display: 'none',
        };
        const credentialPasswordRequired = hasAuthKeyCredential(credential);
        const credentialPasswordMissing = isAuthKeyCredentialPasswordMissing(credential, credentialPassword);
        return (React.createElement("div", { className: classes.container },
            React.createElement("div", { className: classes.avatarContainer },
                React.createElement(Avatar, { participantId: id, size: 60 })),
            React.createElement(Input, { className: classes.bottomMargin, disabled: readOnlyName, id: 'setDisplayName', label: t('profile.setDisplayNameLabel'), name: 'name', onChange: this._onDisplayNameChange, placeholder: t('settings.name'), type: 'text', value: displayName }),
            !hideEmailInSettings && React.createElement("div", { className: 'profile-edit-field' },
                React.createElement(Input, { className: classes.bottomMargin, id: 'setEmail', label: t('profile.setEmailLabel'), name: 'email', onChange: this._onEmailChange, placeholder: t('profile.setEmailInput'), type: 'text', value: email })),
            !isDisplayedOnWelcomePage && React.createElement("div", { className: 'profile-edit-field' },
                React.createElement(Input, { className: classes.bottomMargin, id: 'setCredential', label: t('profile.setCredential'), name: 'credential', onChange: this._onCredentialChange, placeholder: t('settings.credential'), type: 'text', value: credential }),
                React.createElement("input", { accept: '.crev2,.authkey,.credential,.txt', id: 'real-btn-choose-credential', onChange: this._onCredentialFileChange, style: hiddenStyle, type: 'file' }),
                React.createElement(Button, { accessibilityLabel: t('profile.chooseCredentialFile'), id: 'choose-credentail-file', labelKey: 'profile.chooseCredentialFile', onClick: this._onChooseCredentialFileClick, size: 'small' }),
                React.createElement("br", null),
                React.createElement(Input, { bottomLabel: credentialPasswordMissing ? t('profile.credentialPasswordRequired') : undefined, className: classes.bottomMargin, error: credentialPasswordMissing, id: 'setCredentialPassword', label: t('profile.setCredentialPassword'), name: 'password', onChange: this._onCredentialPasswordChange, placeholder: t('settings.credentialPassword'), required: credentialPasswordRequired, type: 'password', value: credentialPassword })),
            authEnabled && this._renderAuth()));
    }
    /**
     * Shows the dialog for logging in or out of a server and closes this
     * dialog.
     *
     * @private
     * @returns {void}
     */
    _onAuthToggle() {
        if (this.props.authLogin) {
            sendAnalytics(createProfilePanelButtonEvent('logout.button'));
            this.props.dispatch(logout());
        }
        else {
            sendAnalytics(createProfilePanelButtonEvent('login.button'));
            this.props.dispatch(login());
        }
    }
    /**
     * Returns a React Element for interacting with server-side authentication.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderAuth() {
        const { authLogin, t } = this.props;
        const classes = withStyles.getClasses(this.props);
        return (React.createElement("div", null,
            React.createElement("h2", { className: classes.label }, t('toolbar.authenticate')),
            authLogin
                && React.createElement("div", { className: classes.name }, t('settings.loggedIn', { name: authLogin })),
            React.createElement(Button, { accessibilityLabel: authLogin ? t('toolbar.logout') : t('toolbar.login'), id: 'login_button', label: authLogin ? t('toolbar.logout') : t('toolbar.login'), onClick: this._onAuthToggle })));
    }
}
export default withStyles(translate(connect()(ProfileTab)), styles);
